import { Card } from '@/components/card';
import { Themes } from '@/components/microstory/theme';
import { useStore } from '@/store';
import { styled } from '@/styles/stitches.config';
import type { I18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useIsomorphicLayoutEffect } from 'framer-motion';
import { useRouter } from 'next/dist/client/router';
import { PointerEvent, useCallback, useEffect, useState } from 'react';
import { AnimatedCard } from './animated-card';
import { CARD_FACTOR, CARD_MAX_WIDTH, GAP_FACTOR, LAYOUT_BREAKPOINT, MAX_GAP } from './constants';
import { DragIndicator } from './drag-indicator';
import { calculateScrollPosition } from './helpers';
import { useDrag } from './use-drag';
import { useGridLayout } from './use-grid-layout';

export type Story = {
  href: string;
  id: string;
  themeLabel: string;
  title: string;
  theme: Themes;
  status?: 'NEW';
};

export const stories = (i18n: I18n) => {
  const storyData: Omit<Story, 'href'>[] = [
    {
      id: 'online-risks',
      themeLabel: t(i18n)({ id: 'overview.online-risks.theme', message: 'Digital tech' }),
      title: t(i18n)({ id: 'overview.online-risks.title', message: 'Online risks' }),
      theme: Themes.fire,
    },
    {
      id: 'online-opportunities',
      themeLabel: t(i18n)({ id: 'overview.online-opportunities.theme', message: 'Digital tech' }),
      title: t(i18n)({
        id: 'overview.online-opportunities.title',
        message: 'Online opportunities',
      }),
      theme: Themes.hutspot,
    },
    {
      id: 'free-to-be-young',
      themeLabel: t(i18n)({ id: 'overview.free-to-be-young.theme', message: 'Agency' }),
      title: t(i18n)({ id: 'overview.free-to-be-young.title', message: 'Free to be young' }),
      theme: Themes.aubergenius,
    },
    {
      id: 'child-power',
      themeLabel: t(i18n)({ id: 'overview.child-power.theme', message: 'Agency' }),
      title: t(i18n)({ id: 'overview.child-power.title', message: 'Child power' }),
      theme: Themes.sunny,
    },
    {
      id: 'equality-for-some',
      themeLabel: t(i18n)({ id: 'overview.equality-for-some.theme', message: 'Discrimination' }),
      title: t(i18n)({ id: 'overview.equality-for-some.title', message: 'Equality for some' }),
      theme: Themes.hutspot,
    },
    {
      id: 'under-pressure',
      themeLabel: t(i18n)({ id: 'overview.under-pressure.theme', message: 'Mental Health' }),
      title: t(i18n)({ id: 'overview.under-pressure.title', message: 'Under pressure' }),
      theme: Themes.merlot,
    },
    {
      id: 'where-you-call-home',
      themeLabel: t(i18n)({ id: 'overview.where-you-call-home.theme', message: 'Globalisation' }),
      title: t(i18n)({ id: 'overview.where-you-call-home.title', message: 'Where you call home' }),
      theme: Themes.spinach,
      status: 'NEW',
    },
    {
      id: 'go-to-sources',
      themeLabel: t(i18n)({ id: 'overview.go-to-sources.theme', message: 'Information' }),
      title: t(i18n)({ id: 'overview.go-to-sources.title', message: 'Go-to sources' }),
      theme: Themes.sky,
      status: 'NEW',
    },
    {
      id: 'the-other-climate-crisis',
      themeLabel: t(i18n)({
        id: 'overview.the-other-climate-crisis.theme',
        message: 'Climate Change',
      }),
      title: t(i18n)({
        id: 'overview.the-other-climate-crisis.title',
        message: 'The other climate crisis',
      }),
      theme: Themes.spinach,
      status: 'NEW',
    },
    {
      id: 'is-scrolling-believing',
      themeLabel: t(i18n)({ id: 'overview.is-scrolling-believing.theme', message: 'Information' }),
      title: t(i18n)({
        id: 'overview.is-scrolling-believing.title',
        message: 'Is scrolling believing?',
      }),
      theme: Themes.aubergenius,
      status: 'NEW',
    },
    {
      id: 'global-citizens',
      themeLabel: t(i18n)({ id: 'overview.global-citizens.theme', message: 'Globalisation' }),
      title: t(i18n)({ id: 'overview.global-citizens.title', message: 'Global citizens' }),
      theme: Themes.sky,
      status: 'NEW',
    },
    {
      id: 'global-outlook',
      themeLabel: t(i18n)({ id: 'overview.global-outlook.theme', message: 'Optimism' }),
      title: t(i18n)({ id: 'overview.global-outlook.title', message: 'Global outlook' }),
      theme: Themes.sky,
    },
    {
      id: 'future-fortunes',
      themeLabel: t(i18n)({ id: 'overview.future-fortunes.theme', message: 'Optimism' }),
      title: t(i18n)({ id: 'overview.future-fortunes.title', message: 'Future fortunes' }),
      theme: Themes.sunny,
    },
    {
      id: 'mental-burdens',
      themeLabel: t(i18n)({ id: 'overview.mental-burdens.theme', message: 'Mental Health' }),
      title: t(i18n)({ id: 'overview.mental-burdens.title', message: 'Mental burdens' }),
      theme: Themes.lime,
    },
    {
      id: 'climate-action',
      themeLabel: t(i18n)({ id: 'overview.climate-action.theme', message: 'Climate Change' }),
      title: t(i18n)({ id: 'overview.climate-action.title', message: 'Climate action' }),
      theme: Themes.fire,
    },
    {
      id: 'childhood-strides',
      themeLabel: t(i18n)({
        id: 'overview.childhood-strides.theme',
        message: 'Childhood progress',
      }),
      title: t(i18n)({ id: 'overview.childhood-strides.title', message: 'Childhood strides' }),
      theme: Themes.lime,
    },
    {
      id: 'eyes-on-equality',
      themeLabel: t(i18n)({ id: 'overview.eyes-on-equality.theme', message: 'Discrimination' }),
      title: t(i18n)({ id: 'overview.eyes-on-equality.title', message: 'Eyes on equality' }),
      theme: Themes.merlot,
    },
  ];

  return storyData.map(
    (d): Story => ({
      ...d,
      href: `/stories/${d.id}`,
    })
  );
};

const Root = styled('div', {
  position: 'relative',
  display: 'flex',
  width: '100%',
  touchAction: 'pan-y',
  backgroundColor: 'white',
  height: 'calc(100vh - var(--header-height))',
  variants: {
    hasNativeScroll: {
      true: {
        overflow: 'visible',
      },
      false: {
        overflow: 'hidden',
      },
    },
    isVisible: {
      true: {
        visibility: 'visible',
      },
      false: {
        visibility: 'hidden',
      },
    },
  },
});

const Overlay = styled('div', {
  position: 'absolute',
  top: 'var(--header-height)',
  left: 0,
  width: '100%',
  height: 'calc(100vh - var(--header-height))',
  backgroundColor: '$unicef',
  zIndex: 1000,
});

const Grid = styled('div', {
  display: 'none',
  '--layout': 'mobile',
  '--card-width': `min(${CARD_MAX_WIDTH}px, ${CARD_FACTOR}vmax)`,
  '--gap': `min(${MAX_GAP}px, ${GAP_FACTOR}vmax)`,
  width: 'var(--card-width)',
  height: 'var(--gap)',

  [`@media screen and (min-width: ${LAYOUT_BREAKPOINT}px)`]: {
    '--layout': `desktop`,
  },
});

const Relative = styled('div', {
  position: 'relative',
});

const noop = () => {
  /*noop*/
};

export default function Overview() {
  const router = useRouter();

  const [root, grid, gridLayout] = useGridLayout<HTMLDivElement, HTMLDivElement>(stories.length);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [selectedPosition, setSelectedPosition] = useState({ x: 0, y: 0 });

  const prefersReducedMotion = useStore((state) => state.prefersReducedMotion);
  const previousRoute = useStore((state) => state.previousRoute);
  const [isIntroAnimation, setIntroAnimation] = useState(
    () => previousRoute === '/' && !prefersReducedMotion
  );
  const [hasIntroAnimationOverlay, setIntroAnimationOverlay] = useState(isIntroAnimation);

  const [isOutroAnimation, setOutroAnimation] = useState(false);

  const [hasDragged, setDragged] = useState(false);
  const { onPointerDown, onPointerMove, onPointerUp, scrollX, scrollY, scrollBase } = useDrag(
    gridLayout,
    root.current
  );

  const isClientSideRendered = previousRoute !== null;
  const hasLayoutInformation = !!(scrollBase.x || scrollBase.y);

  const { i18n } = useLingui();

  const onPointerDownWrapper = useCallback(
    (e: PointerEvent) => {
      if (!hasDragged) {
        setDragged(true);
      }
      onPointerDown(e);
    },
    [hasDragged, onPointerDown]
  );

  // handle pointer up outside of viewport
  useIsomorphicLayoutEffect(() => {
    if (!isOutroAnimation) {
      window.addEventListener('pointerup', onPointerUp);

      return () => {
        window.removeEventListener('pointerup', onPointerUp);
      };
    }
  }, [onPointerUp, isOutroAnimation]);

  // intro animation state handling
  useEffect(() => {
    if (isIntroAnimation && hasLayoutInformation) {
      // we're hiding the overlay after a wee delay that's in conjunction with the intro animation of cards
      const id1 = setTimeout(() => {
        setIntroAnimationOverlay(false);
      }, 220);

      // signifies the end of the intro animation
      // this one could also be implemented differently by relying on callbacks from the animated cards
      // but this keeps it nice and simple
      const id2 = setTimeout(() => {
        setIntroAnimation(false);
      }, 2500);

      return () => {
        clearTimeout(id1);
        clearTimeout(id2);
      };
    }
  }, [isIntroAnimation, hasLayoutInformation]);

  useEffect(() => {
    if (isOutroAnimation) {
      const id = setTimeout(() => {
        router.push(stories(i18n)[selectedIndex].href);
      }, 1500);

      return () => {
        clearTimeout(id);
      };
    }
  }, [isOutroAnimation, selectedIndex, router, i18n]);

  const startOutro = (index: number, href: string) => {
    router.prefetch(href);
    const [x, y] = calculateScrollPosition(index, gridLayout);
    setOutroAnimation(true);
    setSelectedIndex(index);
    setSelectedPosition({ x, y });
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const handleFocus = (index: number) => {
    const [x, y] = calculateScrollPosition(index, gridLayout);
    scrollX.set(x);
    scrollY.set(y);
  };

  return (
    <>
      {hasIntroAnimationOverlay && <Overlay />}

      <Root
        ref={root}
        onPointerDown={isIntroAnimation || isOutroAnimation ? noop : onPointerDownWrapper}
        onPointerMove={isIntroAnimation || isOutroAnimation ? noop : onPointerMove}
        onPointerUp={isIntroAnimation || isOutroAnimation ? noop : onPointerUp}
        hasNativeScroll={!isClientSideRendered && !hasLayoutInformation}
        isVisible={!isClientSideRendered || (isClientSideRendered && hasLayoutInformation)}
      >
        <DragIndicator hidden={hasDragged} />

        {/* We're using this invisible div purely for computing css variables*/}
        <Grid ref={grid}></Grid>

        <Relative>
          {stories(i18n).map((d, i) => (
            <AnimatedCard
              key={d.id}
              scrollX={scrollX}
              scrollY={scrollY}
              cardIndex={i}
              gridLayout={gridLayout}
              scrollBase={scrollBase}
              hasLayoutInformation={hasLayoutInformation}
              isIntroAnimation={isIntroAnimation}
              isOutroAnimation={isOutroAnimation}
              isSelected={i === selectedIndex}
              selectedPosition={selectedPosition}
              handleClick={() => {
                if (prefersReducedMotion) {
                  router.push(d.href);
                } else {
                  startOutro(i, d.href);
                }
              }}
            >
              <Card
                theme={d.theme}
                themeLabel={d.themeLabel}
                href={d.href}
                title={d.title}
                id={d.id}
                handleFocus={() => {
                  handleFocus(i);
                }}
                disableLink
                animateIn={isIntroAnimation}
                animateOut={i === selectedIndex}
                status={d.status}
                png
              />
            </AnimatedCard>
          ))}
        </Relative>
      </Root>
    </>
  );
}
